import { ActionPreviewSection } from '../../../../services/gable/gable.service';

export interface ActionPreviewChange {
    label: string;
    from: string;
    to: string;
}

export interface ActionPreviewFact {
    label: string;
    value: string;
}

const FACT_LINE = /^\s*([^:]{1,40}):\s*(.+?)\s*$/;

/** "Label: value" lines of an info block body, as charge cards carry them; lines that are not one are skipped. */
export function infoBlockFacts(body: string | null | undefined): ActionPreviewFact[] {
    const facts: ActionPreviewFact[] = [];
    for (const line of (body ?? '').split('\n')) {
        const m = line.match(FACT_LINE);
        if (m) {
            facts.push({ label: m[1].trim(), value: m[2] });
        }
    }
    return facts;
}

function fields(sections: ActionPreviewSection[], withSummary = true): Map<string, string> {
    const out = new Map<string, string>();
    const put = (label: string | undefined, value: string | undefined): void => {
        if (label && value !== undefined && value !== null && !out.has(label)) {
            out.set(label, String(value));
        }
    };
    for (const s of sections ?? []) {
        if (s.kind === 'key_value') {
            s.items.forEach((item) => put(item.label, item.value));
        } else if (s.kind === 'outcome') {
            put('Amount', s.amount);
            put('Record', s.record);
        } else if (s.kind === 'status_picker') {
            put(s.label, s.options.find((o) => o.value === s.value)?.label ?? s.value);
        } else if (s.kind === 'info_block') {
            infoBlockFacts(s.body).forEach((fact) => put(fact.label, fact.value));
        } else if (s.kind === 'impact_summary' && withSummary) {
            put(s.label, s.primary);
        }
    }
    return out;
}

/** Label → value fields that differ between an earlier card and its revision, in the revision's order. */
export function diffActionPreviewSections(previous: ActionPreviewSection[], next: ActionPreviewSection[], max = 3): ActionPreviewChange[] {
    const before = fields(previous);
    const changes: ActionPreviewChange[] = [];
    for (const [label, to] of fields(next)) {
        const from = before.get(label);
        if (from !== undefined && from !== to) {
            changes.push({ label, from, to });
        }
        if (changes.length === max) {
            break;
        }
    }
    return changes;
}

/** A few short lines for the dock: the summary first (outcome over impact summary, as the card shows), then "Label: value" facts. */
export function cardFacts(sections: ActionPreviewSection[], max = 4): string[] {
    const out: string[] = [];
    const add = (line: string | undefined): void => {
        const full = line?.trim();
        const text = full && full.length > 72 ? `${full.slice(0, 71).trimEnd()}…` : full;
        if (text && out.length < max && !out.includes(text)) {
            out.push(text);
        }
    };
    const outcomes = (sections ?? []).filter((s) => s.kind === 'outcome');
    const summaries = outcomes.length > 0 ? outcomes : (sections ?? []).filter((s) => s.kind === 'impact_summary');
    for (const s of summaries) {
        add(s.kind === 'outcome' ? s.effect : s.kind === 'impact_summary' ? s.primary : undefined);
    }
    for (const [label, value] of fields(sections, false)) {
        add(`${label}: ${value}`);
    }
    return out;
}
